import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { formatCurrency } from 'utils/formatCurrency';

import { LTLRateMetadata } from '../LTLRateCardMetadata';

interface TurvoRateMetadataTooltipProps {
  rateMetadata: LTLRateMetadata;
  externalId?: string;
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const isTurvoRateMetadata = (rateMetadata: LTLRateMetadata): boolean =>
  rateMetadata.isTurvo === true ||
  isNonEmptyString(rateMetadata.pricingType) ||
  typeof rateMetadata.payable === 'number' ||
  typeof rateMetadata.billable === 'number';

const formatDate = (value?: string): string => {
  if (!isNonEmptyString(value)) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

function MetadataRow({ label, value }: { label: string; value: string }) {
  return (
    <Flex align='center' justify='between' gap='lg' className='w-full'>
      <Typography
        variant='body-xs'
        className='text-neutral-700'
        weight='semibold'
      >
        {label}
      </Typography>
      <Typography
        variant='body-xs'
        className='text-neutral-700 text-right tabular-nums'
      >
        {value}
      </Typography>
    </Flex>
  );
}

export function RateMetadataTooltipContent({
  rateMetadata,
  externalId,
}: TurvoRateMetadataTooltipProps) {
  const payable = formatCurrency(rateMetadata.payable ?? 0, 'USD', 2);
  const billable = formatCurrency(rateMetadata.billable ?? 0, 'USD', 2);
  const margin = formatCurrency(rateMetadata.margin ?? 0, 'USD', 2);
  const pricingType = isNonEmptyString(rateMetadata.pricingType)
    ? rateMetadata.pricingType
    : '--';
  const serviceType = isNonEmptyString(rateMetadata.serviceType)
    ? rateMetadata.serviceType
    : '--';
  const paymentTerms = isNonEmptyString(rateMetadata.paymentTerms)
    ? rateMetadata.paymentTerms
    : '--';
  const serviceArea = isNonEmptyString(rateMetadata.serviceArea)
    ? rateMetadata.serviceArea
    : '--';
  const estimatedDelivery = formatDate(rateMetadata.estimatedDelivery);
  const quoteId = isNonEmptyString(rateMetadata.ltlProviderQuoteId)
    ? rateMetadata.ltlProviderQuoteId
    : isNonEmptyString(externalId)
      ? externalId
      : '--';

  return (
    <Flex direction='col' gap='xs' className='p-2 min-w-[260px]'>
      <MetadataRow label='Quote ID' value={quoteId} />
      <MetadataRow label='Pricing type' value={pricingType} />
      <MetadataRow label='Payable' value={payable} />
      <MetadataRow label='Billable' value={billable} />
      <MetadataRow label='Margin' value={margin} />
      <MetadataRow label='Service type' value={serviceType} />
      <MetadataRow label='Payment terms' value={paymentTerms} />
      <MetadataRow label='Service area' value={serviceArea} />
      <MetadataRow label='Estimated delivery' value={estimatedDelivery} />
    </Flex>
  );
}
